import { useCallback, useEffect, useRef } from 'react';
import { useParcellingStore } from '../store';
import type { ParcelamentoAPI, CreateParcelamentoDTO } from '../types';
import { UserRole } from '@practium/shared';

interface UseParcellingOptions {
	mockData?: ParcelamentoAPI[];
	currentUserId?: string;
	userRole?: UserRole;
	onSuccess?: (message: string) => void;
	onError?: (error: string) => void;
}

const getErrorMessage = (err: unknown, fallback: string): string => {
	if (err instanceof Error) return err.message;
	if (typeof err === 'string') return err;
	return fallback;
};

export const useParcelling = ({ mockData, currentUserId, userRole, onSuccess, onError }: UseParcellingOptions = {}) => {
	const {
		parcellings,
		selectedParcellingId,
		isLoading,
		error,
		setParcellings,
		addParcelling,
		updateParcelling,
		removeParcelling,
		selectParcelling,
		setLoading,
		setError,
		getParcellingById,
		getSelectedParcelling,
		getFilteredParcellings,
	} = useParcellingStore();

	const isAdmin = userRole === UserRole.Admin;

	const selectedParcellingIdRef = useRef(selectedParcellingId);
	selectedParcellingIdRef.current = selectedParcellingId;

	useEffect(() => {
		if (mockData && mockData.length > 0) {
			if (selectedParcellingIdRef.current?.startsWith('temp-')) return;
			setParcellings(mockData);
			selectParcelling(mockData[0]._id);
		}
	}, [mockData]);

	const canEditParcelling = useCallback(
		(parcelling: ParcelamentoAPI): boolean => {
			if (isAdmin) return true;
			if (parcelling._id.startsWith('temp-')) return true;
			if (typeof parcelling.canManage === 'boolean') return parcelling.canManage;
			if (parcelling.isPublic) return false;
			return parcelling.userId === currentUserId;
		},
		[isAdmin, currentUserId]
	);

	const canDeleteParcelling = useCallback(
		(parcelling: ParcelamentoAPI): boolean => {
			if (isAdmin) return true;
			if (parcelling._id.startsWith('temp-')) return true;
			if (typeof parcelling.canManage === 'boolean') return parcelling.canManage;
			if (parcelling.isPublic) return false;
			return parcelling.userId === currentUserId;
		},
		[isAdmin, currentUserId]
	);

	const createParcelling = useCallback(
		async (data: CreateParcelamentoDTO, showToast = true): Promise<ParcelamentoAPI | null> => {
			setLoading(true);
			setError(null);

			try {
				const isPublic = data.isPublic ?? false;
				const newParcelling: ParcelamentoAPI = {
					_id: `temp-${Date.now()}`,
					userId: currentUserId || 'current-user',
					typeParcelling: 'fixed',
					...data,
					isPublic,
					bindingUserIds: isPublic ? [] : (data.bindingUserIds || []),
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};

				addParcelling(newParcelling);
				if (showToast) {
					onSuccess?.('Parcelamento salvo com sucesso!');
				}
				return newParcelling;
			} catch (err) {
				const msg = getErrorMessage(err, 'Erro ao criar parcelamento');
				setError(msg);
				onError?.(msg);
				return null;
			} finally {
				setLoading(false);
			}
		},
		[setLoading, setError, addParcelling, onSuccess, onError, currentUserId, isAdmin, parcellings]
	);

	const updateParcellingById = useCallback(
		async (id: string, data: Partial<ParcelamentoAPI>): Promise<boolean> => {
			setLoading(true);
			setError(null);

			try {
				const existing = getParcellingById(id);
				if (existing && !existing._id.startsWith('temp-') && !canEditParcelling(existing)) {
					throw new Error('Você não tem permissão para editar este parcelamento');
				}

				updateParcelling(id, data);
				onSuccess?.('Parcelamento atualizado com sucesso!');
				return true;
			} catch (err) {
				const msg = getErrorMessage(err, 'Erro ao atualizar parcelamento');
				setError(msg);
				onError?.(msg);
				return false;
			} finally {
				setLoading(false);
			}
		},
		[setLoading, setError, updateParcelling, onSuccess, onError, getParcellingById, canEditParcelling]
	);

	const deleteParcellingById = useCallback(
		async (id: string): Promise<boolean> => {
			setLoading(true);
			setError(null);

			try {
				const existing = getParcellingById(id);
				if (existing && !existing._id.startsWith('temp-') && !canDeleteParcelling(existing)) {
					throw new Error('Você não tem permissão para excluir este parcelamento');
				}

				removeParcelling(id);
				onSuccess?.('Parcelamento excluído com sucesso!');
				return true;
			} catch (err) {
				const msg = getErrorMessage(err, 'Erro ao excluir parcelamento');
				setError(msg);
				onError?.(msg);
				return false;
			} finally {
				setLoading(false);
			}
		},
		[setLoading, setError, removeParcelling, onSuccess, onError, getParcellingById, canDeleteParcelling]
	);

	return {
		parcellings,
		selectedParcelling: getSelectedParcelling(),
		selectedParcellingId,
		isLoading,
		error,
		createParcelling,
		updateParcelling: updateParcellingById,
		deleteParcelling: deleteParcellingById,
		selectParcelling,
		getParcellingById,
		getFilteredParcellings,
		canEditParcelling,
		canDeleteParcelling,
	};
};
